/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {BaseComponent, ConnectivityModule, OnConnectivityChange, RoutingModule, ThunderDispatcher, WrapperProps} from "@intuitionrobotics/thunderstorm/frontend";
import {AccountModule, LoggedStatus, OnLoginStatusUpdated} from "@intuitionrobotics/user-account/frontend";
import {OnUnauthenticatedResponse} from "../index";
import {EnvironmentModule} from "@modules/EnvironmentModule";
import {Dialog_Connectivity} from "./dialogs/Dialog_Connectivity";
import {BadImplementationException} from "@intuitionrobotics/ts-common";
import {registerRoutes, registerValidatingRoutes, Route_Commands, Route_Default, Route_ExternalCommands, Route_Loader, Route_Unsubscribe} from "./Routes";
import {MyToaster} from "./utils/MyToaster";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {getUserId} from "@modules/utils";
import {CookieBanner} from "../widgets/CookieBanner";

export interface OnWindowResize {
    __onWindowResize: () => void
}

const dispatch_OnResize = new ThunderDispatcher<OnWindowResize, "__onWindowResize">("__onWindowResize");

export class App
    extends BaseComponent<WrapperProps>
    implements OnUnauthenticatedResponse, OnLoginStatusUpdated, OnConnectivityChange {

    componentDidMount(): void {
        window.addEventListener("resize", this.onResize);
    }

    componentWillUnmount(): void {
        window.removeEventListener("resize", this.onResize);
    }

    private onResize = () => {
        dispatch_OnResize.dispatchUI();
        dispatch_OnResize.dispatchModule();
    };

    __onConnectivityChange = () => {
        if (!ConnectivityModule.isConnected())
            return Dialog_Connectivity.show();
    };

    onUnauthenticatedResponse = () => {
        AccountModule.logout();
        this.forceUpdate();
    };

    onLoginStatusUpdated = () => {
        if (AccountModule.getLoggedStatus() === LoggedStatus.LOGGED_IN)
        {
            AnalyticsModule.setUserId(getUserId())
            AnalyticsModule.logCareManagerEvent({
                objectType: AnalyticConsts.ObjectTypes.LOGIN,
                entity: AnalyticConsts.Entities.USER,
                activityStatus: AnalyticConsts.ActivityStatus.UPDATE
            });
        }
        this.forceUpdate();
    };

    render() {
        const status = AccountModule.getLoggedStatus();
        switch (status) {
            case LoggedStatus.LOGGED_IN:
                registerRoutes();
                return <>
                    {RoutingModule.getRoutesMap([
                        Route_Unsubscribe,
                        Route_Commands,
                        Route_ExternalCommands,
                        Route_Default
                    ])}
                    <MyToaster/>
                    <CookieBanner/>
                </>

            case LoggedStatus.LOGGED_OUT:
                EnvironmentModule.redirectToLogin();
                return null;

            case LoggedStatus.VALIDATING:
                registerValidatingRoutes()
                return <>
                    {RoutingModule.getRoutesMap([
                        Route_Unsubscribe,
                        Route_Loader
                    ])}
                    <CookieBanner/>
                </>

            default:
                throw new BadImplementationException(`logged status can only be one of 'LOGGED_IN', 'LOGGED_OUT' or 'VALIDATING'`);
        }
    }

}
